import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/shell/page-header';
import { defaultRule } from '@/components/segments/rule-builder';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCreateSegment, useDeleteSegment, useSegments, type Segment } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/segments/')({ component: SegmentsPage });

// The API returns these; `Segment` in queries.ts is the narrow contract the mutations need.
type SegmentRow = Segment & { updated_at?: string };

const NO_AUDIENCE_SIZE =
  'Audience size estimates are not available yet. Rules are evaluated for each customer at delivery time.';

function SegmentsPage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const navigate = useNavigate();
  const { data: segments, isLoading } = useSegments(appId);
  const create = useCreateSegment(appId);
  const remove = useDeleteSegment(appId);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Segment | null>(null);
  const [query, setQuery] = useState('');
  const visible = (segments as SegmentRow[] | undefined)?.filter((segment) =>
    segment.name.toLowerCase().includes(query.toLowerCase())
  );

  const onCreate = (): void => {
    create.mutate(
      { name: name.trim(), definition: defaultRule() },
      {
        onSuccess: (segment) => {
          setOpen(false);
          setName('');
          void navigate({ to: '/segments/$id', params: { id: segment.id } });
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  const onDelete = (segment: Segment): void => {
    remove.mutate(segment.id, {
      // A segment a campaign still points at comes back 409 with a message that says exactly that.
      // Showing "Something went wrong" instead would hide the one thing the author can act on.
      onError: (error) => toast.error(error.message),
      onSuccess: () => toast.success(`Deleted “${segment.name}”`),
      onSettled: () => setPendingDelete(null),
    });
  };

  const newSegmentDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>New audience</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New audience</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="segment-name">Name</Label>
          <Input
            id="segment-name"
            value={name}
            placeholder="Paying users"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button onClick={onCreate} disabled={name.trim() === '' || create.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audiences"
        description="Define reusable customer groups from profile traits and product events."
        actions={segments && segments.length > 0 ? newSegmentDialog : undefined}
      />

      {isLoading && <Skeleton className="h-40 w-full" />}

      {segments && segments.length === 0 && (
        <EmptyState
          title="No audiences yet"
          description="Create a reusable set of trait and event conditions, then target it from a campaign."
          action={newSegmentDialog}
          note={NO_AUDIENCE_SIZE}
        />
      )}

      {segments && segments.length > 0 && (
        <div className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search audiences" className="pl-9" />
          </div>
          <div className="overflow-hidden rounded-lg border">
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible?.map((segment) => (
              <TableRow key={segment.id}>
                <TableCell>
                  <Link
                    to="/segments/$id"
                    params={{ id: segment.id }}
                    className="font-medium hover:underline"
                  >
                    {segment.name}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {segment.updated_at ? new Date(segment.updated_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${segment.name}`}
                    onClick={() => setPendingDelete(segment)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
          {!visible?.length ? <p className="border-t px-4 py-10 text-center text-sm text-muted-foreground">No audiences match “{query}”.</p> : null}
          </div>
        </div>
      )}

      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. An audience a campaign still targets cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && onDelete(pendingDelete)}
              disabled={remove.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
